import axios from "axios";
import React, { useEffect, useState } from "react";
import Loader from "../../components/Loader/Loader";
import Pagination from "../../components/Pagination/Pagination";
import { getAuthHeaders } from "../../utils/auth";
import { formatUsDateFromDateTime } from "../../utils/usDateFormat";

const LocationManagement = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locLatitude, setLocLatitude] = useState("");
  const [locLongitude, setLocLongitude] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const statusOptions = ["All", "Active", "Inactive"];
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [detailLocation, setDetailLocation] = useState(null);

  const formatSyncDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const datePart = formatUsDateFromDateTime(value);
    if (!datePart) return "-";
    const pad = (num) => String(num).padStart(2, "0");
    const hh = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${datePart} ${hh}:${min}`;
  };

  const fetchLocations = async (page = 1) => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/system/locations-with-coordinates`,
        {
          headers,
          params: {
            page,
            page_size: pageSize,
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(statusFilter !== "All" ? { status: statusFilter } : {}),
          },
        }
      );
      setLocations(response.data.locations || []);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error("Error fetching locations:", error);
      alert(error.response?.data?.detail || "Failed to fetch locations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchLocations(currentPage);
  }, [currentPage, debouncedSearch, statusFilter]);

  const openEditModal = (location) => {
    setSelectedLocation(location);
    setLocLatitude(
      location.loc_latitude !== null && location.loc_latitude !== undefined
        ? String(location.loc_latitude)
        : ""
    );
    setLocLongitude(
      location.loc_longitude !== null && location.loc_longitude !== undefined
        ? String(location.loc_longitude)
        : ""
    );
    setIsActive(location.status === "Active");
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setSelectedLocation(null);
    setLocLatitude("");
    setLocLongitude("");
    setIsActive(true);
  };

  const handleSave = async () => {
    if (!selectedLocation?.id) return;
    if (locLatitude !== "") {
      const latValue = Number(locLatitude);
      if (!Number.isFinite(latValue) || latValue < -90 || latValue > 90) {
        alert("Latitude must be a number between -90 and 90.");
        return;
      }
    }
    if (locLongitude !== "") {
      const lonValue = Number(locLongitude);
      if (!Number.isFinite(lonValue) || lonValue < -180 || lonValue > 180) {
        alert("Longitude must be a number between -180 and 180.");
        return;
      }
    }
    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/system/locations/${selectedLocation.id}/coordinates`,
        {
          loc_latitude: locLatitude === "" ? null : Number(locLatitude),
          loc_longitude: locLongitude === "" ? null : Number(locLongitude),
          active: Boolean(isActive),
        },
        { headers }
      );
      await fetchLocations(currentPage);
      closeEditModal();
    } catch (error) {
      console.error("Error updating location coordinates:", error);
      alert(error.response?.data?.detail || "Failed to update location coordinates.");
    } finally {
      setSaving(false);
    }
  };

  const LocationIcon = () => (
    <svg
      width="1.5em"
      height="1.5em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 1 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );

  const handleStatusChange = (status) => {
    setCurrentPage(1);
    setStatusFilter(status);
  };

  return (
    <div className="p-4 sm:p-5 w-full min-w-0 max-w-6xl mx-auto">
      <div className="section-header !md:flex-row !flex-row !items-center bg-[#674936] text-white p-4 rounded">
        <h3 className="flex gap-4 text-white text-[1rem] md:text-[1.6rem] font-bold">
          <LocationIcon /> Location Management
        </h3>
      </div>
      <div className="my-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {statusOptions.map((status) => (
            <button
              key={status}
              onClick={() => handleStatusChange(status)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${statusFilter === status
                ? "bg-white text-[#674936] shadow"
                : "bg-[#674936] text-white hover:bg-[#ebebeb] hover:shadow hover:text-[#674936]"
                }`}
            >
              {status}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 w-full min-w-0 max-w-full sm:max-w-[400px] flex-1 sm:flex-initial">
          <div className="form-field w-full relative">
            <input
              type="text"
              placeholder="Search location..."
              value={searchTerm}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchTerm(e.target.value);
              }}
              className="w-full !pr-[20px] border rounded focus:outline-none focus:border-gray-500"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setCurrentPage(1);
                  setSearchTerm("");
                }}
                className="absolute right-[20px] top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="relative overflow-x-auto border border-gray-200 rounded-lg w-full shadow-sm mx-auto mt-4">
        <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
          <thead className="bg-[#674936]">
            <tr>
              <th className="px-4 py-3 text-white font-semibold">Location</th>
              <th className="px-4 py-3 text-white font-semibold">Latitude</th>
              <th className="px-4 py-3 text-white font-semibold">Longitude</th>
              <th className="px-4 py-3 text-white font-semibold">Loc Latitude</th>
              <th className="px-4 py-3 text-white font-semibold">Loc Longitude</th>
              <th className="px-4 py-3 text-white font-semibold">Status</th>
              <th className="px-4 py-3 text-white font-semibold">Sync Date</th>
              <th className="px-4 py-3 text-white font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-500">
                  <Loader />
                </td>
              </tr>
            ) : locations.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-500">
                  No locations found.
                </td>
              </tr>
            ) : (
              locations.map((location) => (
                <tr
                  key={location.id}
                  className="bg-white hover:bg-gray-50 cursor-pointer"
                  onClick={() => setDetailLocation(location)}
                >
                  <td className="px-4 py-3">{location.name || "-"}</td>
                  <td className="px-4 py-3">{location.latitude ?? "-"}</td>
                  <td className="px-4 py-3">{location.longitude ?? "-"}</td>
                  <td className="px-4 py-3">{location.loc_latitude ?? "-"}</td>
                  <td className="px-4 py-3">{location.loc_longitude ?? "-"}</td>
                  <td className="px-4 py-3">{location.status || "-"}</td>
                  <td className="px-4 py-3">{formatSyncDate(location.sync_date)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(location);
                      }}
                      className="bg-[#674936] text-white px-3 py-1 rounded text-xs hover:bg-[#503220]"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="p-4 flex justify-center">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-2 sm:items-center sm:py-6">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md mx-4 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain min-w-0 my-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit Coordinates</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Loc Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={locLatitude}
                  onChange={(e) => setLocLatitude(e.target.value)}
                  className="mt-1 w-full border rounded px-3 py-2"
                  placeholder="e.g. 29.289127"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Loc Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={locLongitude}
                  onChange={(e) => setLocLongitude(e.target.value)}
                  className="mt-1 w-full border rounded px-3 py-2"
                  placeholder="e.g. 47.92065"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded bg-[#674936] text-white hover:bg-[#503220]"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {detailLocation && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-2 sm:items-center sm:py-6"
          onClick={() => setDetailLocation(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain min-w-0 my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#674936] text-white px-5 py-4 rounded-t-lg flex items-center justify-between">
              <h3 className="text-lg font-semibold truncate">{detailLocation.name || "Location Details"}</h3>
              <button
                onClick={() => setDetailLocation(null)}
                className="text-white hover:text-gray-200 ml-2 flex-shrink-0"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <DetailField label="Location Name" value={detailLocation.name} />
                <DetailField label="Status" value={
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${detailLocation.status === "Active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {detailLocation.status || "-"}
                  </span>
                } />
                <DetailField label="Dynamics ID" value={detailLocation.dynamics_id} />
                <DetailField label="Location ID" value={detailLocation.location_id} />
                <DetailField label="Brand ID" value={detailLocation.brand_id} />
                <DetailField label="Country ID" value={detailLocation.country_id} />
              </div>

              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Coordinates</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm bg-gray-50 rounded-lg p-3">
                  <DetailField label="Latitude (Dynamics)" value={detailLocation.latitude} />
                  <DetailField label="Longitude (Dynamics)" value={detailLocation.longitude} />
                  <DetailField label="Custom Latitude" value={detailLocation.loc_latitude} />
                  <DetailField label="Custom Longitude" value={detailLocation.loc_longitude} />
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sync Info</h4>
                <div className="text-sm bg-gray-50 rounded-lg p-3">
                  <DetailField label="Last Synced" value={formatSyncDate(detailLocation.sync_date)} />
                </div>
              </div>

              {(detailLocation.created_by || detailLocation.updated_by) && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Audit Trail</h4>
                  <div className="space-y-3">
                    {detailLocation.created_by && (
                      <div className="bg-gray-50 rounded-lg p-3 text-sm">
                        <p className="text-xs font-semibold text-gray-500 mb-1">Created By</p>
                        <p className="font-medium text-gray-800">
                          {detailLocation.created_by.name || "-"}
                        </p>
                        <p className="text-gray-500">{detailLocation.created_by.email || "-"}</p>
                        {detailLocation.created_by.employee_no && (
                          <p className="text-gray-500">Emp# {detailLocation.created_by.employee_no}</p>
                        )}
                        {detailLocation.created_at && (
                          <p className="text-gray-400 text-xs mt-1">{formatSyncDate(detailLocation.created_at)}</p>
                        )}
                      </div>
                    )}
                    {detailLocation.updated_by && (
                      <div className="bg-gray-50 rounded-lg p-3 text-sm">
                        <p className="text-xs font-semibold text-gray-500 mb-1">Last Updated By</p>
                        <p className="font-medium text-gray-800">
                          {detailLocation.updated_by.name || "-"}
                        </p>
                        <p className="text-gray-500">{detailLocation.updated_by.email || "-"}</p>
                        {detailLocation.updated_by.employee_no && (
                          <p className="text-gray-500">Emp# {detailLocation.updated_by.employee_no}</p>
                        )}
                        {detailLocation.updated_at && (
                          <p className="text-gray-400 text-xs mt-1">{formatSyncDate(detailLocation.updated_at)}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="px-5 pb-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setDetailLocation(null);
                  openEditModal(detailLocation);
                }}
                className="px-4 py-2 rounded bg-[#674936] text-white text-sm hover:bg-[#503220]"
              >
                Edit
              </button>
              <button
                onClick={() => setDetailLocation(null)}
                className="px-4 py-2 rounded border border-gray-300 text-gray-700 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailField = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-gray-800 font-medium break-all">
      {value !== null && value !== undefined && value !== "" ? value : "-"}
    </p>
  </div>
);

export default LocationManagement;
